import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

// Async fetch
export const fetchPropertyCommissions = createAsyncThunk(
  "propertyCommissions/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/property-commissions");
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to fetch property commissions"
      );
    }
  }
);

export const fetchEffectivePropertyCommission = createAsyncThunk(
  "propertyCommissions/fetchEffective",
  async (propertyId, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/property-commissions/effective", {
        params: { property_id: propertyId },
      });
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to preview effective commission rates");
    }
  }
);

export const fetchHeldPropertyCommissions = createAsyncThunk(
  "propertyCommissions/fetchHeld",
  async ({ page = 1, status = "held" } = {}, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/property-commission-snapshots", {
        params: { status, page },
      });
      const payload = res.data?.data ?? res.data;
      if (Array.isArray(payload)) return { items: payload, meta: res.data?.meta || res.data };
      return { items: payload?.data ?? [], meta: payload };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to fetch held commissions");
    }
  }
);

export const resolveHeldPropertyCommission = createAsyncThunk(
  "propertyCommissions/resolveHeld",
  async ({ id, commission_value, reason }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/property-commission-snapshots/${id}/resolve`, { commission_value, reason });
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to resolve held commission");
    }
  }
);

export const recordCommissionRefund = createAsyncThunk(
  "propertyCommissions/recordRefund",
  async ({ paymentId, reason, refund_reference }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/payments/${paymentId}/refund`, { reason, refund_reference });
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to record full refund");
    }
  }
);

// Async add
export const addPropertyCommission = createAsyncThunk(
  "propertyCommissions/add",
  async (data, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/property-commissions", data);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to add property commission"
      );
    }
  }
);

// Async update
export const updatePropertyCommission = createAsyncThunk(
  "propertyCommissions/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await api.put(`/api/property-commissions/${id}`, data);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to update property commission"
      );
    }
  }
);

// Async delete
export const deletePropertyCommission = createAsyncThunk(
  "propertyCommissions/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/api/property-commissions/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || "Failed to delete property commission"
      );
    }
  }
);

const propertyCommissionSlice = createSlice({
  name: "propertyCommissions",
  initialState: {
    list: [],
    status: "idle",
    error: null,
    effective: null,
    effectiveStatus: "idle",
    held: [],
    heldStatus: "idle",
    heldMeta: { current_page: 1, last_page: 1 },
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchPropertyCommissions.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPropertyCommissions.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload;
      })
      .addCase(fetchPropertyCommissions.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      .addCase(fetchEffectivePropertyCommission.pending, (state) => {
        state.effectiveStatus = "loading";
      })
      .addCase(fetchEffectivePropertyCommission.fulfilled, (state, action) => {
        state.effectiveStatus = "succeeded";
        state.effective = action.payload;
      })
      .addCase(fetchEffectivePropertyCommission.rejected, (state, action) => {
        state.effectiveStatus = "failed";
        state.error = action.payload;
      })
      .addCase(fetchHeldPropertyCommissions.pending, (state) => {
        state.heldStatus = "loading";
      })
      .addCase(fetchHeldPropertyCommissions.fulfilled, (state, action) => {
        state.heldStatus = "succeeded";
        state.held = action.payload.items;
        state.heldMeta = action.payload.meta || { current_page: 1, last_page: 1 };
      })
      .addCase(fetchHeldPropertyCommissions.rejected, (state, action) => {
        state.heldStatus = "failed";
        state.error = action.payload;
      })
      .addCase(resolveHeldPropertyCommission.fulfilled, (state, action) => {
        const resolvedId = action.meta.arg.id;
        state.held = state.held.filter((item) => item.id !== resolvedId);
      })

      // Add
      .addCase(addPropertyCommission.fulfilled, (state, action) => {
        state.list.push(action.payload);
      })
      .addCase(addPropertyCommission.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Update
      .addCase(updatePropertyCommission.fulfilled, (state, action) => {
        const index = state.list.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
      })
      .addCase(updatePropertyCommission.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Delete
      .addCase(deletePropertyCommission.fulfilled, (state, action) => {
        state.list = state.list.filter((item) => item.id !== action.payload);
      })
      .addCase(deletePropertyCommission.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError } = propertyCommissionSlice.actions;
export default propertyCommissionSlice.reducer;
